import {
  ref,
  reactive,
  watch,
  Ref,
  computed,
} from 'vue'
import i18next from 'i18next'
import { isNetworkDisconnect } from 'www/utils'
import getUserLanguage from 'src/utils/getUserLanguage'
import scrollLock from 'src/utils/scrollLock'
import apiDoctorCases from 'www/vue_components/common/DoctorCases/api/apiDoctorCases'
import { DIALOG_PROPS } from 'www/vue_components/common/DoctorCases/constants'
import type { ApiDoctorCasesResponseGet } from 'www/vue_components/common/DoctorCases/api/apiDoctorCases.types'

const userLanguage = getUserLanguage()

export default function useCases({
  doctorId,
  alertDialogProps,
  alertDialogActive,
}: {
  doctorId: number
  alertDialogProps: Ref<typeof DIALOG_PROPS[keyof typeof DIALOG_PROPS]>
  alertDialogActive: Ref<boolean>
}) {
  const isLoading = ref(false)
  const isVisibleModal = ref(false)
  const page = ref(1)
  const hasNextPage = ref(true)
  const doctorCases = ref<ApiDoctorCasesResponseGet['results']>([])
  const dialogProps = computed(() => ({
    default: {
      ...DIALOG_PROPS.default,
      title: i18next.t(DIALOG_PROPS.default.title, { ns: userLanguage }),
      contentText: i18next.t(DIALOG_PROPS.default.contentText, { ns: userLanguage }),
      confirmText: i18next.t(DIALOG_PROPS.default.confirmText, { ns: userLanguage }),
    },
    disconnect: {
      ...DIALOG_PROPS.disconnect,
      title: i18next.t(DIALOG_PROPS.disconnect.title, { ns: userLanguage }),
      contentText: i18next.t(DIALOG_PROPS.disconnect.contentText, { ns: userLanguage }),
      confirmText: i18next.t(DIALOG_PROPS.disconnect.confirmText, { ns: userLanguage }),
    },
  }))

  async function loadNextCases() {
    if (isLoading.value || !hasNextPage.value) {
      return
    }

    isLoading.value = true

    try {
      const { results, has_next: hasNext } = await apiDoctorCases.get({ doctorId, page: page.value })

      // eslint-disable-next-line require-atomic-updates
      hasNextPage.value = hasNext
      doctorCases.value.push(...results)
      page.value++
    } catch (error: any) {
      const { response, message } = error || {}
      const { status } = response || {}

      alertDialogActive.value = true
      isVisibleModal.value = false

      if (isNetworkDisconnect({ status, message })) {
        alertDialogProps.value = dialogProps.value.disconnect
        return
      }

      alertDialogProps.value = dialogProps.value.default
    } finally {
      // eslint-disable-next-line require-atomic-updates
      isLoading.value = false
    }
  }

  function closeModal() {
    isVisibleModal.value = false
  }

  async function openModal() {
    isVisibleModal.value = true

    if (!doctorCases.value.length) {
      await loadNextCases()
    }
  }

  watch(isVisibleModal, value => {
    scrollLock.set(value)
  })

  return reactive({
    doctorCases,
    isLoading,
    isVisibleModal,
    hasNextPage,
    loadNextCases,
    openModal,
    closeModal,
  })
}
